import React from 'react';

import { formatDateHandler } from 'utils/common';
import { getStatusBadge, getIssuanceStatusLabel } from '../../constants';
import { replacementStatus, daysUntilReplacement } from './grouping';

/**
 * One garment, as a row.
 *
 * The photograph is a recognition aid — "is this the jacket?" — so it is a
 * thumbnail beside the name rather than the body of a card. It sits in a fixed
 * box with `loading="lazy"`, so a slow image never reflows the list under a
 * thumb that is already reaching for something.
 */

const dueLabel = (item, today) => {
  const status = replacementStatus(item.next_replacement_date, today);
  if (status === 'ok') return null;
  if (status === 'overdue') return { className: 'myu-due-overdue', text: 'Overdue' };

  const days = daysUntilReplacement(item.next_replacement_date, today);
  if (days === 0) return { className: 'myu-due-soon', text: 'Due today' };
  return { className: 'myu-due-soon', text: `Due in ${days} ${days === 1 ? 'day' : 'days'}` };
};

/**
 * The one-line description of a garment: size, how many, when it arrived.
 *
 * Shared by the row and the pending card because it is the same sentence in
 * both, and two copies of it drift. Quantity is stated even when it is one —
 * an employee should not have to infer the count from a missing word — but a
 * line the server sent no quantity for says nothing rather than "Qty".
 */
export const UniformMetaLine = ({ item }) => {
  const size = item.size_detail ? `${item.size_detail.name} (${item.size_detail.code})` : null;
  const hasQuantity = item.quantity !== null && item.quantity !== undefined;

  return (
    <p className="myu-meta">
      {size && <span className="myu-meta-part">Size {size}</span>}
      {size && hasQuantity && <span className="myu-meta-sep">·</span>}
      {hasQuantity && <span className="myu-meta-part">Qty {item.quantity}</span>}
      {(size || hasQuantity) && <span className="myu-meta-sep">·</span>}
      <span className="myu-meta-part">Issued {formatDateHandler(item.issuance_date)}</span>
    </p>
  );
};

export const UniformThumb = ({ item }) => (
  <div className="myu-thumb">
    {item.uniform_item_image ? (
      // The name is announced on the very next line, so the image adds nothing
      // for a screen reader and is marked decorative instead of repeating it.
      <img src={item.uniform_item_image} alt="" loading="lazy" />
    ) : (
      <i className="fa fa-shopping-bag" aria-hidden="true" />
    )}
  </div>
);

const UniformRow = ({ item, today }) => {
  const due = dueLabel(item, today);

  return (
    <div className="myu-row">
      <UniformThumb item={item} />

      <div className="myu-body">
        <p className="myu-name">{item.uniform_item_name}</p>
        <UniformMetaLine item={item} />
      </div>

      <div className="myu-aside">
        <span style={getStatusBadge(item.status)}>{getIssuanceStatusLabel(item.status)}</span>
        {due && <span className={`myu-due ${due.className}`}>{due.text}</span>}
      </div>
    </div>
  );
};

export default UniformRow;
